import { SelectChangeEvent } from '@mui/material';
import {
  ControlModeTemplateField,
  ControlModeTemplateFieldsType,
  INumberInputFieldControlModeTemplate,
  ISelectInputFieldControlModeTemplate,
} from '@src/types/ControlModes';
import React, { useCallback } from 'react';
import { useFormContext } from '../context/FormContext';
import { FormActionType } from '../context/formReducer';
import { createField } from '../helpers/fieldCreators';
import { validateNewField } from '../helpers/fieldValidators';

// Hook for field-related operations
export function useFields() {
  const { state, dispatch } = useFormContext();

  // Extract field-related state
  const {
    showAddField,
    isEditMode,
    editingField,
    selectedSettingIndex,
    newFieldData,
    newFieldErrors,
    parsedOptions,
    newOption,
    unsavedSettings,
  } = state;

  // Handle field type change
  const handleFieldTypeChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const type = event.target.value as ControlModeTemplateFieldsType;

      // Set the appropriate default value based on type
      let defaultValue: string | number | null | boolean = '';
      if (type === ControlModeTemplateFieldsType.NUMBER) {
        defaultValue = 0;
      } else if (type === ControlModeTemplateFieldsType.BOOLEAN) {
        defaultValue = false;
      } else if (type === ControlModeTemplateFieldsType.SELECT) {
        defaultValue = '';
      }

      dispatch({
        type: FormActionType.SET_NEW_FIELD_DATA,
        payload: { type, defaultValue },
      });
    },
    [dispatch],
  );

  // Handle new field change
  const handleNewFieldChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, checked, type: inputType } = event.target;

      // Handle different input types
      let newValue: string | number | boolean = value;
      if (inputType === 'checkbox') {
        newValue = checked;
      } else if (inputType === 'number') {
        newValue = value === '' ? '' : Number(value);
      }

      dispatch({
        type: FormActionType.SET_NEW_FIELD_DATA,
        payload: { [name]: newValue },
      });
    },
    [dispatch],
  );

  // Handle new range change
  const handleNewRangeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      const numValue = value === '' ? undefined : Number(value);

      // Map the input field names to the correct range property names
      const rangeKey =
        name === 'rangeMin' ? 'min' : name === 'rangeMax' ? 'max' : name;

      dispatch({
        type: FormActionType.SET_NEW_FIELD_DATA,
        payload: {
          range: {
            ...newFieldData.range,
            [rangeKey]: numValue,
          },
        },
      });
    },
    [dispatch, newFieldData.range],
  );

  // Handle new field toggle
  const handleNewFieldToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;

      dispatch({
        type: FormActionType.SET_NEW_FIELD_DATA,
        payload: { quickConfig: checked },
      });
    },
    [dispatch],
  );

  // Handle add option
  const handleAddOption = useCallback(() => {
    dispatch({ type: FormActionType.ADD_OPTION });
  }, [dispatch]);

  // Handle remove option
  const handleRemoveOption = useCallback(
    (optionToRemove: string) => {
      dispatch({ type: FormActionType.REMOVE_OPTION, payload: optionToRemove });
    },
    [dispatch],
  );

  // Toggle add field
  const toggleAddField = useCallback(() => {
    dispatch({ type: FormActionType.TOGGLE_ADD_FIELD });
  }, [dispatch]);

  // Set show add field
  const setShowAddField = useCallback(
    (show: boolean) => {
      dispatch({ type: FormActionType.SET_SHOW_ADD_FIELD, payload: show });
    },
    [dispatch],
  );

  // Start edit field
  const startEditField = useCallback(
    (field: ControlModeTemplateField) => {
      // Find the setting that contains this field
      if (!unsavedSettings) {
        return;
      }

      let settingIndex = -1;
      for (let i = 0; i < unsavedSettings.length; i++) {
        const setting = unsavedSettings[i];
        const fieldIndex = setting.fields.findIndex(
          (f) => f.controllerField === field.controllerField,
        );

        if (fieldIndex !== -1) {
          settingIndex = i;
          break;
        }
      }

      if (settingIndex === -1) {
        return;
      }

      // Set editing state
      dispatch({ type: FormActionType.SET_IS_EDIT_MODE, payload: true });
      dispatch({ type: FormActionType.SET_EDITING_FIELD, payload: field });
      dispatch({
        type: FormActionType.SET_SELECTED_SETTING_INDEX,
        payload: settingIndex,
      });
      dispatch({ type: FormActionType.SET_SHOW_ADD_FIELD, payload: true });

      // Populate form with field data
      const fieldData: {
        uiLabel: string;
        controllerField: string;
        type: ControlModeTemplateFieldsType;
        defaultValue: string | number | boolean;
        quickConfig: boolean;
        range?: { min?: number; max?: number };
        listOptions?: string;
      } = {
        uiLabel: field.uiLabel,
        controllerField: field.controllerField,
        type: field.type,
        defaultValue: field.defaultValue || '',
        quickConfig: field.quickConfig,
      };

      // Add type-specific properties
      if (field.type === ControlModeTemplateFieldsType.NUMBER) {
        const numberField = field as INumberInputFieldControlModeTemplate;
        fieldData.range = numberField.range || {
          min: undefined,
          max: undefined,
        };
      } else if (field.type === ControlModeTemplateFieldsType.SELECT) {
        const selectField = field as ISelectInputFieldControlModeTemplate;
        fieldData.listOptions = selectField.listOptions.join(',');

        // Ensure the defaultValue is set to one of the options
        if (
          selectField.defaultValue &&
          selectField.listOptions.includes(selectField.defaultValue.toString())
        ) {
          fieldData.defaultValue = selectField.defaultValue;
        } else if (selectField.listOptions.length > 0) {
          fieldData.defaultValue = selectField.listOptions[0];
        }

        // Update parsed options
        dispatch({
          type: FormActionType.SET_PARSED_OPTIONS,
          payload: selectField.listOptions,
        });
      }

      dispatch({ type: FormActionType.SET_NEW_FIELD_DATA, payload: fieldData });
    },
    [dispatch, unsavedSettings],
  );

  // Add a new field
  const addNewField = useCallback(() => {
    if (!unsavedSettings) {
      return;
    }

    // Validate field data
    const errors: Record<string, string> = {};

    // Basic validation for required fields
    if (!newFieldData.uiLabel.trim()) {
      errors.uiLabel = 'Label is required';
    }

    if (!newFieldData.controllerField.trim()) {
      errors.controllerField = 'Controller Field is required';
    } else {
      // Check if the controller field already exists in any setting
      const fieldExists = unsavedSettings.some((setting) =>
        setting.fields.some(
          (field) =>
            field.controllerField === newFieldData.controllerField &&
            (!editingField ||
              field.controllerField !== editingField.controllerField),
        ),
      );

      if (fieldExists) {
        errors.controllerField = 'Controller Field must be unique';
      }
    }

    // Use validateNewField for type-specific validation
    const validationResult = validateNewField(newFieldData, parsedOptions);

    // Merge validation errors
    Object.assign(errors, validationResult.errors);

    if (Object.keys(errors).length > 0) {
      dispatch({ type: FormActionType.SET_NEW_FIELD_ERRORS, payload: errors });
      dispatch({
        type: FormActionType.SHOW_SNACKBAR,
        payload: {
          message: 'Please correct the errors before adding the field',
          severity: 'error',
        },
      });
      return;
    }

    // Create the field
    let newField: ControlModeTemplateField;

    if (newFieldData.type === ControlModeTemplateFieldsType.SELECT) {
      newField = createField(
        {
          uiLabel: newFieldData.uiLabel,
          controllerField: newFieldData.controllerField,
          type: newFieldData.type,
          quickConfig: newFieldData.quickConfig,
          defaultValue: String(
            newFieldData.defaultValue !== undefined
              ? newFieldData.defaultValue
              : '',
          ),
        },
        parsedOptions,
      );
    } else if (newFieldData.type === ControlModeTemplateFieldsType.NUMBER) {
      // Ensure range has proper types if it exists
      const range = newFieldData.range
        ? {
            min: newFieldData.range.min,

            max: newFieldData.range.max,
          }
        : undefined;

      // For number fields, ensure defaultValue is a number
      // Note: fieldCreators.ts expects defaultValue to be a string, but will convert it to a number for number fields
      const defaultValue = newFieldData.defaultValue;

      newField = createField({
        uiLabel: newFieldData.uiLabel,
        controllerField: newFieldData.controllerField,
        type: newFieldData.type,
        quickConfig: newFieldData.quickConfig,
        defaultValue,
        range,
      });
    } else {
      // For other field types (STRING, BOOLEAN)
      newField = createField({
        uiLabel: newFieldData.uiLabel,
        controllerField: newFieldData.controllerField,
        type: newFieldData.type,
        quickConfig: newFieldData.quickConfig,
        defaultValue: String(
          newFieldData.defaultValue !== undefined
            ? newFieldData.defaultValue
            : '',
        ),
      });
    }

    // Update the settings
    const updatedSettings = [...unsavedSettings];
    const settingToUpdate = updatedSettings[selectedSettingIndex];

    if (isEditMode && editingField) {
      // Find the original setting that contains the field being edited
      let originalSettingIndex = -1;
      for (let i = 0; i < updatedSettings.length; i++) {
        const setting = updatedSettings[i];
        const fieldIndex = setting.fields.findIndex(
          (f) => f.controllerField === editingField.controllerField,
        );

        if (fieldIndex !== -1) {
          originalSettingIndex = i;
          break;
        }
      }

      // If the original setting is found and it's different from the selected setting
      if (
        originalSettingIndex !== -1 &&
        originalSettingIndex !== selectedSettingIndex
      ) {
        // Remove the field from the original setting
        const originalSetting = updatedSettings[originalSettingIndex];
        originalSetting.fields = originalSetting.fields.filter(
          (f) => f.controllerField !== editingField.controllerField,
        );

        // Add the field to the selected setting
        settingToUpdate.fields.push(newField);
      } else {
        // Update the field in the current setting
        const fieldIndex = settingToUpdate.fields.findIndex(
          (f) => f.controllerField === editingField.controllerField,
        );

        if (fieldIndex !== -1) {
          settingToUpdate.fields[fieldIndex] = newField;
        }
      }

      dispatch({
        type: FormActionType.SET_UNSAVED_SETTINGS,
        payload: updatedSettings,
      });
      dispatch({
        type: FormActionType.SHOW_SNACKBAR,
        payload: {
          message: `Field "${newField.uiLabel}" updated. Click Save to apply changes.`,
          severity: 'info',
        },
      });
    } else {
      // Add a new field
      settingToUpdate.fields.push(newField);
      dispatch({
        type: FormActionType.SET_UNSAVED_SETTINGS,
        payload: updatedSettings,
      });
      dispatch({
        type: FormActionType.SHOW_SNACKBAR,
        payload: {
          message: `Field "${newField.uiLabel}" added. Click Save to apply changes.`,
          severity: 'info',
        },
      });
    }

    // Update grouped fields
    const updatedGroupedFields = state.groupedFields.map((group) => {
      // Find the corresponding setting for this group
      const matchingSetting = updatedSettings.find(
        (setting) => setting.controllerField === group.controllerField,
      );

      // If a matching setting is found, update the group's fields
      if (matchingSetting) {
        return { ...group, fields: matchingSetting.fields };
      }

      return group;
    });

    dispatch({
      type: FormActionType.SET_GROUPED_FIELDS,
      payload: updatedGroupedFields,
    });

    if (newField.defaultValue !== undefined) {
      dispatch({
        type: FormActionType.UPDATE_FORM_VALUE,
        payload: {
          name: newField.controllerField,
          value: newField.defaultValue,
          defaultValue: newField.defaultValue,
        },
      });
    }

    // Clear any "must have at least one field" error for this setting
    if (state.fieldErrors && Object.keys(state.fieldErrors).length > 0) {
      const settingToUpdate = updatedSettings[selectedSettingIndex];
      const settingErrorKey = `setting_${settingToUpdate.controllerField}`;

      if (
        state.fieldErrors[settingErrorKey] &&
        state.fieldErrors[settingErrorKey].includes(
          'must have at least one field',
        )
      ) {
        const updatedErrors = { ...state.fieldErrors };
        delete updatedErrors[settingErrorKey];

        dispatch({
          type: FormActionType.SET_FIELD_ERRORS,
          payload: updatedErrors,
        });
      }
    }

    // Reset form
    dispatch({ type: FormActionType.RESET_NEW_FIELD_DATA });
    dispatch({ type: FormActionType.SET_NEW_FIELD_ERRORS, payload: {} });
    dispatch({ type: FormActionType.SET_SHOW_ADD_FIELD, payload: false });
    dispatch({ type: FormActionType.SET_IS_EDIT_MODE, payload: false });
    dispatch({ type: FormActionType.SET_EDITING_FIELD, payload: null });
  }, [
    dispatch,
    unsavedSettings,
    newFieldData,
    parsedOptions,
    selectedSettingIndex,
    isEditMode,
    editingField,
    state.groupedFields,
    state.fieldErrors,
  ]);

  // Set selected setting index
  const setSelectedSettingIndex = useCallback(
    (index: number) => {
      dispatch({
        type: FormActionType.SET_SELECTED_SETTING_INDEX,
        payload: index,
      });
    },
    [dispatch],
  );

  // Set editing field
  const setEditingField = useCallback(
    (field: ControlModeTemplateField | null) => {
      dispatch({ type: FormActionType.SET_EDITING_FIELD, payload: field });
    },
    [dispatch],
  );

  // Set is edit mode
  const setIsEditMode = useCallback(
    (isEdit: boolean) => {
      dispatch({ type: FormActionType.SET_IS_EDIT_MODE, payload: isEdit });
    },
    [dispatch],
  );

  // Set new option
  const setNewOption = useCallback(
    (option: string) => {
      dispatch({ type: FormActionType.SET_NEW_OPTION, payload: option });
    },
    [dispatch],
  );

  // Reset field form
  const resetFieldForm = useCallback(() => {
    dispatch({ type: FormActionType.RESET_NEW_FIELD_DATA });
    dispatch({ type: FormActionType.SET_NEW_FIELD_ERRORS, payload: {} });
    dispatch({ type: FormActionType.SET_SHOW_ADD_FIELD, payload: false });
    dispatch({ type: FormActionType.SET_IS_EDIT_MODE, payload: false });
    dispatch({ type: FormActionType.SET_EDITING_FIELD, payload: null });
  }, [dispatch]);

  return {
    // State
    showAddField,
    isEditMode,
    editingField,
    selectedSettingIndex,
    newFieldData,
    newFieldErrors,
    parsedOptions,
    newOption,

    // Handlers
    handleFieldTypeChange,
    handleNewFieldChange,
    handleNewRangeChange,
    handleNewFieldToggle,
    handleAddOption,
    handleRemoveOption,
    toggleAddField,
    setShowAddField,
    startEditField,
    addNewField,
    setSelectedSettingIndex,
    setEditingField,
    setIsEditMode,
    setNewOption,
    resetFieldForm,
  };
}
